// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, saveToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1738654942907-e79039dc247f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxmbG9yaWRhJTIwY29hc3RhbCUyMGZhY2lsaXR5fGVufDB8fHx8MTc4NzA2ODk4OXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState("psingh@acehcs.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const { refresh } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      saveToken(data.token);
      await refresh();
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50" data-testid="login-page">
      <div className="hidden md:block relative overflow-hidden">
        <img src={HERO} alt="Florida coast" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <Logo variant="dark" size="lg" showTagline />
          <div className="max-w-md">
            <div className="uppercase tracking-widest text-xs font-mono-plex text-white/70 mb-3">Florida · SNF & ALF</div>
            <h1 className="font-heading font-black text-4xl leading-tight mb-3">
              Behavioral health, coordinated across every facility you serve.
            </h1>
            <p className="text-white/80 text-sm leading-relaxed">
              Track visits, run SWOT, and understand the geopolitics behind every corporate owner — in one CRM built for your ten-person team.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md p-8 border-slate-200 shadow-none">
          <div className="md:hidden mb-6">
            <Logo size="md" />
          </div>
          <div className="mb-6">
            <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-2">Sign in</div>
            <h2 className="font-heading text-3xl font-bold">Welcome back</h2>
            <p className="text-sm text-slate-500 mt-2">Access your Florida facilities intelligence.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 transition-colors duration-200">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-right">
              <a href="/forgot-password" data-testid="forgot-link" className="text-xs text-slate-500 hover:text-slate-900 font-mono-plex">Forgot password?</a>
            </div>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <div className="text-[11px] uppercase tracking-widest font-mono-plex text-slate-400">Or</div>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button variant="outline" onClick={google} data-testid="google-signin" className="w-full border-slate-300 transition-colors duration-200 hover:bg-slate-50">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.28-1.93-6.14-4.52H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.86 14.11a6.98 6.98 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.68-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.68 2.84C6.72 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="mt-6 text-xs text-slate-400 font-mono-plex">
            Admin? Use your acehcs.com credentials.
          </div>
        </Card>
      </div>
    </div>
  );
}
